import React, { useState } from 'react';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { RevenuePayload } from '../api';
import { Card, CardTitle, Skeleton, usd } from './ui';

const FEE_COLORS = ['#ba9965', '#a49b94'];

const tooltipStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 10,
  fontSize: 12,
  color: 'var(--color-on-surface)',
  boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
  padding: '8px 10px',
};

const FeeBreakdownCard: React.FC<{
  data: RevenuePayload | null;
  loading: boolean;
}> = ({ data, loading }) => {
  const [showOnboarding, setShowOnboarding] = useState(true);
  const [showAum, setShowAum] = useState(true);
  const fb = data?.feeBreakdown;

  const allFees: Array<{
    product: string;
    revenueUsd: number;
    hasData: boolean;
    checked: boolean;
    setChecked: (v: boolean) => void;
  }> = [
    {
      product: 'Onboarding Fees',
      revenueUsd: fb?.onboardingFeesUsd ?? 0,
      hasData: !!fb?.onboardingFeesUsd,
      checked: showOnboarding,
      setChecked: setShowOnboarding,
    },
    {
      product: 'AUM Management Fees',
      revenueUsd: fb?.aumFeesUsd ?? 0,
      hasData: fb?.aumFeesUsd != null,
      checked: showAum,
      setChecked: setShowAum,
    },
  ];

  const activeItems = allFees.filter((f) => f.checked && f.hasData);
  const total = activeItems.reduce((s, f) => s + f.revenueUsd, 0);

  return (
    <Card>
      <CardTitle title='Fee Revenue' source='Stripe · Billing CSV' />
      <div className='flex items-center gap-4'>
        <div className='relative shrink-0' style={{ width: 150 }}>
          {activeItems.length > 0 ? (
            <ResponsiveContainer width='100%' height={184}>
              <PieChart>
                <Pie
                  data={activeItems}
                  dataKey='revenueUsd'
                  nameKey='product'
                  innerRadius={52}
                  outerRadius={74}
                  paddingAngle={2}
                  stroke='none'
                  isAnimationActive={false}
                >
                  {activeItems.map((f) => {
                    const i = allFees.findIndex((a) => a.product === f.product);
                    return <Cell key={f.product} fill={FEE_COLORS[i % FEE_COLORS.length]} />;
                  })}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} formatter={(v: any) => usd(Number(v))} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className='flex h-[184px] items-center justify-center'>
              <div
                className='rounded-full border-[22px]'
                style={{
                  width: 148,
                  height: 148,
                  borderColor: 'var(--color-surface-tertiary)',
                }}
              />
            </div>
          )}
          <div className='pointer-events-none absolute inset-0 flex flex-col items-center justify-center'>
            {loading ? (
              <Skeleton className='h-5 w-16' />
            ) : (
              <>
                <span className='figure text-base font-semibold text-on-surface'>
                  {usd(total, true)}
                </span>
                <span className='text-[10px] uppercase tracking-wide text-on-surface-quaternary'>
                  total
                </span>
              </>
            )}
          </div>
        </div>

        <ul className='min-w-0 flex-1 space-y-3 text-xs text-on-surface-secondary'>
          {allFees.map((f, i) => (
            <li key={f.product} className='flex items-start gap-2'>
              <input
                type='checkbox'
                className='mt-0.5 rounded'
                style={{ accentColor: FEE_COLORS[i % FEE_COLORS.length] }}
                checked={f.checked}
                disabled={!f.hasData}
                onChange={(e) => f.setChecked(e.target.checked)}
              />
              <span
                className='mt-0.5 h-2 w-2 shrink-0 rounded-sm'
                style={{ background: FEE_COLORS[i % FEE_COLORS.length] }}
              />
              <div className='min-w-0 flex-1'>
                <div className='truncate' title={f.product}>
                  {f.product}
                </div>
                {loading ? (
                  <Skeleton className='mt-1 h-3 w-16' />
                ) : f.hasData ? (
                  <div className='tabular-nums text-on-surface-tertiary'>{usd(f.revenueUsd)}</div>
                ) : (
                  <div className='text-on-surface-tertiary'>No data yet</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
};

export default FeeBreakdownCard;
